import { useEffect, useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, notification, Row, Select, Space } from 'antd';
import Typography from 'antd/es/typography';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useLoaderData, useSearchParams } from 'react-router-dom';

import { getHomePage, getInvalidateCache } from '../../../api/Get';
import { postSaveManageApp } from '../../../api/Post';
import SearchCollection from '../../collections/SearchCollection';
import SearchSerie from '../../series/SearchSerie';

import { DEFAULT_CLASSIFICATION, getHomePageLabel, HOME_PAGES } from './homePages';
import RowCategory from './RowCategory';

const { Text } = Typography;

const manageCategoriesLoader = async ({ request }) => {
  const classification = new window.URL(request.url).searchParams.get('classification') ?? undefined;
  const details = await getHomePage(classification);
  return details ?? { cats: [], types: [] };
};

const ManageCategories = () => {
  const { cats, classification = DEFAULT_CLASSIFICATION, types } = useLoaderData();
  const [, setSearchParams] = useSearchParams();
  const [notificationApi, contextHolder] = notification.useNotification();

  // console.log(cats);
  // console.log(types);

  const [isLoading, setIsLoading] = useState(false);
  const [itemList, setItemList] = useState(cats);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCollectionModalOpen, setCollectionModalOpen] = useState(false);
  const [catSelected, setCatSelected] = useState(0);

  // Le loader est relancé à chaque changement de home : on repart des données chargées
  useEffect(() => {
    setItemList(cats);
    setCatSelected(0);
  }, [cats]);

  const onChangeHomePage = value => {
    if (value === classification) return;
    Modal.confirm({
      cancelText: 'Annuler',
      content: 'Les modifications non enregistrées seront perdues.',
      okText: 'Changer',
      onOk: () => setSearchParams({ classification: String(value) }),
      title: `Passer à la home "${getHomePageLabel(value)}" ?`,
    });
  };

  const save = async () => {
    setIsLoading(true);
    const getNewCatsName = cat => {
      const inputValue = document.getElementById(`section-name-${cat.uid}`).value;
      if (inputValue.trim().length <= 0 || inputValue.trim().length > 255 || cat.name === inputValue) return cat.name;
      return inputValue.trim();
    };

    const getSubtitle = cat => {
      const input = document.getElementById(`section-subtitle-${cat.uid}`);
      if (input) {
        const { value } = input;
        if (value.trim().length <= 0 || value.trim().length > 255 || cat.subtitle === value) return cat.subtitle;
        return value.trim();
      }
      return '';
    };

    const jsonToSave = itemList
      .filter(c => getNewCatsName(c).length > 0)
      .map((c, index) => {
        return {
          content: c.content.map((s, sIndex) => {
            return {
              id: s.id || s.name,
              position: sIndex,
            };
          }),
          imgType: c.imgType,
          newName: getNewCatsName(c),
          oldName: c.name || getNewCatsName(c),
          position: index,
          subtitle: getSubtitle(c),
          typeContent: c.typeContent,
        };
      });

    console.log(jsonToSave);

    const response = await postSaveManageApp(jsonToSave, classification);
    setIsLoading(false);

    if (response?.status === 200) {
      notificationApi.success({
        message: `Home "${getHomePageLabel(classification)}" enregistrée`,
        placement: 'topLeft',
      });
    } else if (response?.status === 409) {
      notificationApi.error({
        description: response.data.conflicts.map(c => `"${c.name}" (${getHomePageLabel(c.classification)})`).join(', '),
        duration: 0,
        message: 'Nom de section déjà utilisé par une autre home',
        placement: 'topLeft',
      });
    } else {
      notificationApi.error({
        message: "Erreur lors de l'enregistrement",
        placement: 'topLeft',
      });
    }
  };

  const onChangeImgType = (indexCat, imgType) => {
    const newItemList = Array.from(itemList);
    newItemList[indexCat].imgType = imgType;
    setItemList(newItemList);
  };

  const onChangeContentType = (indexCat, imgType) => {
    const newItemList = Array.from(itemList);
    newItemList[indexCat].typeContent = imgType;
    setItemList(newItemList);
  };

  const onDragEnd = result => {
    const { destination, source, type } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    if (type === 'column') {
      const newColumnOrder = Array.from(itemList);
      const section = newColumnOrder.splice(source.index, 1);
      newColumnOrder.splice(destination.index, 0, ...section);
      setItemList(newColumnOrder);
      return;
    }

    if (destination.droppableId === source.droppableId) {
      const indexSection = itemList.findIndex(section => section.uid === destination.droppableId);
      if (indexSection >= 0) {
        const newItemList = Array.from(itemList);
        const newTaskIds = Array.from(newItemList[indexSection].content);
        const serie = newTaskIds.splice(source.index, 1);
        newTaskIds.splice(destination.index, 0, ...serie);
        newItemList[indexSection].content = newTaskIds;

        setItemList(newItemList);
      }
      return;
    }

    const indexSourceSection = itemList.findIndex(section => section.uid === source.droppableId);
    const indexDestinationSection = itemList.findIndex(section => section.uid === destination.droppableId);

    if (indexSourceSection >= 0 && indexDestinationSection >= 0) {
      const newItemList = Array.from(itemList);

      const newSeriesSource = Array.from(newItemList[indexSourceSection].content);
      const serieToMove = newSeriesSource.splice(source.index, 1);

      const newSeriesDestination = Array.from(newItemList[indexDestinationSection].content);
      newSeriesDestination.splice(destination.index, 0, ...serieToMove);

      newItemList[indexSourceSection].content = newSeriesSource;
      newItemList[indexDestinationSection].content = newSeriesDestination;

      setItemList(newItemList);
    }
  };

  const onAddSerie = serie => {
    const newItemList = Array.from(itemList);
    // const newTaskIds = Array.from(newItemList[catSelected].content);
    // newTaskIds.push(serie);
    // newItemList[catSelected].content = newTaskIds;
    newItemList[catSelected].content = [serie, ...newItemList[catSelected].content];

    setItemList(newItemList);
  };

  const onCancelModal = () => {
    setIsModalOpen(false);
    setCollectionModalOpen(false);
  };

  const onDeleteCat = index => {
    const newColumnOrder = Array.from(itemList);
    newColumnOrder.splice(index, 1);
    setItemList(newColumnOrder);
  };

  const onDeleteSerie = (indexSection, indexSerie) => {
    const newItemList = Array.from(itemList);
    const newTaskIds = Array.from(newItemList[indexSection].content);
    newTaskIds.splice(indexSerie, 1);
    newItemList[indexSection].content = newTaskIds;

    setItemList(newItemList);
  };

  const showModal = index => {
    setCatSelected(index);
    setIsModalOpen(true);
  };
  const showCollectionModal = index => {
    setCatSelected(index);
    setCollectionModalOpen(true);
  };

  const AddRowCatVisual = () => {
    return (
      <div
        style={{
          alignItems: 'center',
          backgroundColor: '#00cca3',
          borderRadius: 100,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          margin: 30,
          padding: 40,
        }}
        onClick={() => {
          const newItemList = Array.from(itemList);
          newItemList.push({
            classification,
            content: [],
            imgType: 8,
            ImgType: {
              idTypeImg: 8,
              nameType: 'Default',
            },
            name: '',
            position: itemList.length,
            typeContent: 'serie',
            uid: `nouvellesection#${new Date().getTime()}`,
          });
          setItemList(newItemList);
        }}>
        <PlusOutlined style={{ color: '#120D35', fontSize: 20 }} />
      </div>
    );
  };

  const handleInvalidateCache = async () => {
    setIsLoading(true);
    await getInvalidateCache();
    setIsLoading(false);
  };

  return (
    <>
      {contextHolder}
      <Text>Modifier la home page de l'application</Text>
      <Row style={{ marginBottom: 10, marginTop: 10 }}>
        <Flex style={{ justifyContent: 'space-between' }}>
          <Flex>
            <Select
              value={classification}
              style={{ marginRight: 10, width: 260 }}
              options={HOME_PAGES}
              onChange={onChangeHomePage}
            />
            <Button loading={isLoading} onClick={save} type="primary">
              Enregistrer
            </Button>
          </Flex>
          <Flex>
            <Button style={{ marginLeft: 10 }} onClick={handleInvalidateCache}>
              Reset cache mobile
            </Button>
          </Flex>
        </Flex>
      </Row>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="all-columns" direction="vertical" type="column">
          {provided => {
            return (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <Space direction="vertical" style={{ display: 'flex' }}>
                  {itemList.map((section, index) => {
                    return (
                      <RowCategory
                        key={`${section.uid}`}
                        index={index}
                        section={section}
                        types={types}
                        onChangeImgType={onChangeImgType}
                        onChangeContentType={onChangeContentType}
                        onAddSerie={showModal}
                        onAddCollection={showCollectionModal}
                        onDeleteCat={onDeleteCat}
                        onDeleteSerie={onDeleteSerie}
                      />
                    );
                  })}
                  <AddRowCatVisual />
                </Space>
                {provided.placeholder}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
      <Modal
        title={catSelected < itemList.length ? `Ajouter une série à ${itemList[catSelected].name}` : 'Ajouter une série'}
        open={isModalOpen}
        onCancel={onCancelModal}
        width={'70vw'}
        footer={[
          <Button key="back" onClick={onCancelModal}>
            Fermer
          </Button>,
        ]}>
        <div
          style={{
            backgroundColor: '#F5F5F5',
            maxHeight: '60vh',
            overflow: 'scroll',
          }}>
          <SearchSerie type="add" onClickAdd={onAddSerie} />
        </div>
      </Modal>
      <Modal
        title={catSelected < itemList.length ? `Ajouter une collection à ${itemList[catSelected].name}` : 'Ajouter une collection'}
        open={isCollectionModalOpen}
        onCancel={onCancelModal}
        width={'70vw'}
        footer={[
          <Button key="back" onClick={onCancelModal}>
            Fermer
          </Button>,
        ]}>
        <div
          style={{
            backgroundColor: '#F5F5F5',
            maxHeight: '60vh',
            overflow: 'scroll',
          }}>
          <SearchCollection type="add" onClickAdd={onAddSerie} />
        </div>
      </Modal>
    </>
  );
};

export { ManageCategories, manageCategoriesLoader };
